import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { TurnstileWidget } from "@/components/TurnstileWidget";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Shield, CheckCircle } from "lucide-react";
import { SECURITY_CONFIG } from "@/config/security";

interface CloudflareGateProps {
  children: React.ReactNode;
  onVerified?: () => void;
}

export const CloudflareGate = ({ children, onVerified }: CloudflareGateProps) => {
  const [isVerified, setIsVerified] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [verificationSuccess, setVerificationSuccess] = useState(false);

  useEffect(() => {
    // Skip Turnstile verification in development mode
    if (!SECURITY_CONFIG.turnstile.enabled) {
      console.log('Turnstile disabled in development mode');
      setIsVerified(true);
      setIsChecking(false);
      onVerified?.();
      return;
    }

    // Check if user was already verified in this session
    const verified = sessionStorage.getItem('cloudflare_verified');
    const verifiedTime = sessionStorage.getItem('cloudflare_verified_time');
    
    if (verified === 'true' && verifiedTime) {
      const timeDiff = Date.now() - parseInt(verifiedTime);
      // Verification valid for 30 minutes
      if (timeDiff < 30 * 60 * 1000) {
        setIsVerified(true);
        setIsChecking(false);
        return;
      }
    }
    
    setIsChecking(false);
  }, []);

  useEffect(() => {
    if (turnstileToken) {
      handleVerification();
    }
  }, [turnstileToken]);

  const handleVerification = async () => {
    if (!turnstileToken) return;

    try {
      // Store verification in session
      sessionStorage.setItem('cloudflare_verified', 'true');
      sessionStorage.setItem('cloudflare_verified_time', Date.now().toString());
      sessionStorage.setItem('cloudflare_token', turnstileToken);

      setVerificationSuccess(true);
      
      // Wait a moment to show success message
      setTimeout(() => {
        setIsVerified(true);
        onVerified?.();
      }, 1500);
    } catch (error) {
      console.error('Verification error:', error);
    }
  };

  if (isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-900">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Checking your browser...</p>
        </div>
      </div>
    );
  }

  if (!isVerified) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-900">
        <div className="text-center">
          <div className="mb-8">
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2">
              Checking your browser
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              This process is automatic. Your browser will redirect shortly.
            </p>
          </div>

          {verificationSuccess ? (
            <div className="flex flex-col items-center">
              <CheckCircle className="h-12 w-12 text-green-500 mb-4" />
              <p className="text-green-600 dark:text-green-400 font-medium">
                Verification successful
              </p>
            </div>
          ) : (
            <div className="flex flex-col items-center space-y-6">
              <TurnstileWidget
                sitekey={SECURITY_CONFIG.turnstile.siteKey}
                onVerify={setTurnstileToken}
                theme="auto"
                size="normal"
              />
              <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                <Shield className="h-4 w-4" />
                <span>Protected by Cloudflare</span>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return <>{children}</>;
};
